import { faceNodeGetVolumeNode } from "building-generation/buildingGraph/faceNodeParent";
import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { AnalyticMaterial, BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import { isBuildingGeomParentTag } from "building-types/buildingGeomTag/buildingGeomTags";
import {
  type BuildingGeomParent,
  buildingGeomAttributeTagNew,
  EXTERIOR_FINISH_TAG,
  INTERIOR_FINISH_TAG,
} from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { WallSweepSystem } from "building-types/facades/facadeTypes";
import type { FacesWithMaterial } from "building-types/geometry/facesWithMaterial";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { RoofWallFaces } from "building-types/roofs/generatedRoofSystemTypes";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import { WallType } from "building-types/walls/wallEnums";
import type { WallFinishFaces } from "building-types/walls/wallFinishFaces";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWall } from "building-types/walls/wallTypes";
import type { RWTable } from "common/data/table";
import { percentNew, worldInchesNew } from "common/utils/nominalNumbers";
import { materialSrcFromPalette } from "doc/materialLookup";
import { aabb2Grow } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import {
  brepNew,
  faceCentroid,
  faceTopoNew,
  tagFace,
  transformFaceLoopBasis,
  type Extrusion,
  type Face,
} from "geom/brep";
import { distPointToSegment } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import { line2Normal } from "geom/line2";
import {
  planeCanonicalFromSegment,
  planeHash,
  planeIsHorizontal,
  planeNewXY,
  planeNormal,
  planeProject,
} from "geom/plane";
import { polyLine2Copy, polyLine2EdgesOverlap } from "geom/polyLine2";
import { unionCoplanarFaces } from "geom/unionFaces";
import type { UVTransformation } from "geom/uv";
import { WallDatumAbstractionProvider } from "../elements/datums/datumAbstractionProviders";
import { collectWallFinishFaces2 } from "../elements/wallAssemblies/wallFinishFaces";
import { hashMaterialWithUV } from "../generationTools/hashMaterialWithUV";
import { addAnalyticBRep, addAnalyticExtrusion, addAnalyticFace } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

const defaultExteriorFinishFaceStyle = {
  color: 0xbb88bb,
  opacity: percentNew(0.75),
  lineWidth3d: worldInchesNew(0.01),
};

const defaultInteriorFinishFaceStyle = {
  color: 0xccdddd,
  edgeColor: 0xccaaaa,
  opacity: percentNew(1),
  lineWidth3d: worldInchesNew(0.01),
};

const select = makeBuildingSelector([
  "datumAbstractions",
  "enabledFeatures",
  "levels",
  "levelsAttributes",
  "walls",
  "wallSweeps",
  "rooms",
  "roofSystems",
  "settings",
  "exteriorPalette",
  "interiorPalette",
  "mirror",
  "doc",
  "buildingGraph",
  "wallPlanes",
]);

const compare = makeGenerationStageComparator<typeof select>({
  walls: "shallow",
  roofSystems: "shallow",
});

function generate(params: ReturnType<typeof select>): Pick<Building, "analyticWallEntities"> {
  const newEntities: RWTable<BuildingAnalyticEntity> = {};

  const {
    datumAbstractions,
    enabledFeatures,
    levels,
    levelsAttributes,
    walls,
    wallSweeps,
    rooms,
    roofSystems,
    settings: { wallCatalog: wallCatalogSettings, wall: wallSettings },
    exteriorPalette,
    interiorPalette,
    mirror,
    buildingGraph,
  } = params;

  const wallPlanesOn = enabledFeatures.wallPlanes ?? false;

  const { exteriorWallMaterial } = wallSettings;

  const collections: GeometryCollections = {
    exteriorExtrusionsByMaterialUV: {},
    exteriorSideFacesByMaterialUV: {},
    exteriorCapFacesByMaterialUV: {},
    interiorByRoomAndMaterial: {},
  };

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(levels, levelsAttributes, datumAbstractions);

  collectLOD2WallGeomFromWallSystems(
    wallDatumAbstractionProvider,
    walls,
    rooms,
    wallSweeps,
    wallCatalogSettings,
    mirror,
    wallPlanesOn,
    buildingGraph,
    levels,
    collections,
  );

  for (const roofSystemId in roofSystems) {
    const roofSystem = roofSystems[roofSystemId];
    for (const roofId in roofSystem.roofs) {
      if (roofSystem.roofs[roofId].roofWallSystem) {
        const { wallMaterial = exteriorWallMaterial, wallFaces, roofWallSystem } = roofSystem.roofs[roofId];

        if (roofWallSystem) {
          collectLOD2WallGeomFromWallSystem(
            wallDatumAbstractionProvider,
            roofWallSystem.wallSystem,
            rooms,
            wallSweeps[roofWallSystem.levelId],
            wallCatalogSettings,
            mirror,
            wallPlanesOn,
            buildingGraph,
            levels,
            roofWallSystem.levelId,
            collections,
          );
        }

        const { startWallFaces, endWallFaces } = wallFaces;

        collectFacesFromRoofWallFaces(wallMaterial, undefined, startWallFaces, collections);
        collectFacesFromRoofWallFaces(wallMaterial, undefined, endWallFaces, collections);
      }
    }
  }

  for (const materialUVHash in collections.exteriorExtrusionsByMaterialUV) {
    const extrusionsWithMaterial = collections.exteriorExtrusionsByMaterialUV[materialUVHash];
    for (const extrusion of extrusionsWithMaterial.geom) {
      const materialId = extrusionsWithMaterial.material;

      addAnalyticExtrusion(
        "ANALYTIC_WALL",
        newEntities,
        "Exterior Wall Finish Extrusion",
        [],
        extrusion,
        materialId ? materialSrcFromPalette(exteriorPalette, materialId) : undefined,
        [EXTERIOR_FINISH_TAG],
        defaultExteriorFinishFaceStyle,
      );
    }
  }

  const exteriorCapFaceIndex = buildExteriorCapFaceIndex(collections.exteriorCapFacesByMaterialUV);

  for (const materialUVHash in collections.exteriorSideFacesByMaterialUV) {
    const facesWithMaterials = collections.exteriorSideFacesByMaterialUV[materialUVHash];

    const materialId = facesWithMaterials.material;
    const materialTransformation = facesWithMaterials.materialTransformation;
    const materialSrc = materialId ? materialSrcFromPalette(exteriorPalette, materialId) : undefined;

    for (const face of unionCoplanarFaces(Array.from(facesWithMaterials.geom))) {
      const wallFinishParentIds = wallFinishParentIdsFromFace(face);
      const brepFaces: Face[] = [face];
      const materialTransformations: (UVTransformation | undefined)[] = [materialTransformation];

      const relatedCapFacesByMaterialUV = relatedExteriorCapFacesByMaterialUV(
        exteriorCapFaceIndex,
        wallFinishParentIds,
      );
      for (const capMaterialUVHash in relatedCapFacesByMaterialUV) {
        const capFacesWithMaterial = relatedCapFacesByMaterialUV[capMaterialUVHash];
        const capMaterialTransformation = capFacesWithMaterial.materialTransformation;

        const capFaces = unionCoplanarFaces(
          Array.from(capFacesWithMaterial.geom).filter((capFace) => exteriorCapFaceSharesSideFaceEdge(capFace, face)),
        );
        brepFaces.push(...capFaces);
        materialTransformations.push(...capFaces.map(() => capMaterialTransformation));
      }

      addAnalyticBRep(
        "ANALYTIC_WALL",
        newEntities,
        "Exterior Wall Finish",
        [],
        brepNew(brepFaces, brepFaces.map(faceTopoNew)),
        materialSrc,
        materialTransformations,
        face,
        [EXTERIOR_FINISH_TAG],
        defaultExteriorFinishFaceStyle,
      );
    }
  }

  for (const roomMaterialHash in collections.interiorByRoomAndMaterial) {
    const facesWithMaterials = collections.interiorByRoomAndMaterial[roomMaterialHash];
    const room = rooms[facesWithMaterials.roomId];
    const level = room?.level;
    const roomHandle = room?.roomHandle;
    const levelHandle = level?.handle;

    let materialInfo: AnalyticMaterial | undefined;
    const materialId = facesWithMaterials.material;
    if (materialId) {
      materialInfo = {
        materialSrc: materialSrcFromPalette(interiorPalette, materialId, room?.type),
        materialTransformation: facesWithMaterials.materialTransformation,
      };
    }

    const roomTypeTag = room?.type ? buildingGeomAttributeTagNew("roomType", room.type, "ROOM_TYPE") : undefined;
    for (const face of unionCoplanarFaces(Array.from(facesWithMaterials.geom))) {
      if (roomTypeTag) {
        tagFace(face, roomTypeTag);
      }
      addAnalyticFace(
        "ANALYTIC_WALL",
        newEntities,
        "Interior Wall Finish",
        [roomHandle, levelHandle].filter((h) => !!h),
        face,
        materialInfo,
        [INTERIOR_FINISH_TAG],
        defaultInteriorFinishFaceStyle,
      );
    }
  }

  return {
    analyticWallEntities: newEntities,
  };
}

export default memo(select, generate, compare);

interface GeomSetWithMaterial<T extends Face | Extrusion> {
  readonly material?: PaletteMaterialSlot;
  readonly materialTransformation?: UVTransformation;
  readonly geom: Set<T>;
}

interface GeometryCollections {
  readonly exteriorExtrusionsByMaterialUV: Record<string, GeomSetWithMaterial<Extrusion>>;
  readonly exteriorSideFacesByMaterialUV: Record<string, GeomSetWithMaterial<Face>>;
  readonly exteriorCapFacesByMaterialUV: Record<string, GeomSetWithMaterial<Face>>;
  readonly interiorByRoomAndMaterial: Record<string, GeomSetWithMaterial<Face> & { readonly roomId: string }>;
}

type ExteriorCapFaceIndex = Record<string, Record<string, GeomSetWithMaterial<Face>>>;

function wallFinishParentIdsFromFace(face: Face): Set<string> {
  const wallFinishParentIds = new Set<string>();

  (face.tags ?? [])
    .filter(
      (tag): tag is BuildingGeomParent =>
        isBuildingGeomParentTag(tag) && (tag.parentType === "WALL_MASS" || tag.parentType === "WALL_LAYER"),
    )
    .forEach((tag) => {
      wallFinishParentIds.add(tag.parentId);
    });

  return wallFinishParentIds;
}

function exteriorCapFaceSharesSideFaceEdge(capFace: Face, sideFace: Face): boolean {
  const capLoopsInSideFacePlane = capFace.loops.map((loop) =>
    transformFaceLoopBasis(polyLine2Copy(loop), capFace.plane, sideFace.plane),
  );

  for (const capLoop of capLoopsInSideFacePlane) {
    for (const sideLoop of sideFace.loops) {
      if (polyLine2EdgesOverlap(capLoop, sideLoop)) {
        return true;
      }
    }
  }

  return false;
}

function addExteriorCapFaceToIndex(
  capFaceIndex: ExteriorCapFaceIndex,
  materialUVHash: string,
  facesWithMaterial: GeomSetWithMaterial<Face>,
  face: Face,
) {
  if (!capFaceIndex[materialUVHash]) {
    capFaceIndex[materialUVHash] = {};
  }

  for (const wallFinishParentId of wallFinishParentIdsFromFace(face)) {
    addGeomWithMaterial(
      facesWithMaterial.material,
      facesWithMaterial.materialTransformation,
      face,
      capFaceIndex[materialUVHash],
      wallFinishParentId,
    );
  }
}

function buildExteriorCapFaceIndex(
  exteriorCapFacesByMaterialUV: Record<string, GeomSetWithMaterial<Face>>,
): ExteriorCapFaceIndex {
  const capFaceIndex: ExteriorCapFaceIndex = {};

  for (const materialUVHash in exteriorCapFacesByMaterialUV) {
    const facesWithMaterial = exteriorCapFacesByMaterialUV[materialUVHash];
    for (const face of facesWithMaterial.geom) {
      addExteriorCapFaceToIndex(capFaceIndex, materialUVHash, facesWithMaterial, face);
    }
  }

  return capFaceIndex;
}

function relatedExteriorCapFacesByMaterialUV(
  capFaceIndex: ExteriorCapFaceIndex,
  wallFinishParentIds: Set<string>,
): Record<string, GeomSetWithMaterial<Face>> {
  const relatedCapFacesByMaterialUV: Record<string, GeomSetWithMaterial<Face>> = {};
  const seen = new Set<Face>();

  for (const materialUVHash in capFaceIndex) {
    const capFacesByWallFinishParentId = capFaceIndex[materialUVHash];
    for (const wallFinishParentId of wallFinishParentIds) {
      const capFaces = capFacesByWallFinishParentId[wallFinishParentId];
      if (!capFaces) {
        continue;
      }
      for (const face of capFaces.geom) {
        if (seen.has(face)) {
          continue;
        }
        seen.add(face);
        addGeomWithMaterial(
          capFaces.material,
          capFaces.materialTransformation,
          face,
          relatedCapFacesByMaterialUV,
          materialUVHash,
        );
      }
    }
  }

  return relatedCapFacesByMaterialUV;
}

export type WallSideRoomCache = {
  leftRoomId: string | undefined;
  rightRoomId: string | undefined;
  midX: number;
  midY: number;
  rightNormalX: number;
  rightNormalY: number;
};

type WallCacheEntry = {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  cache: WallSideRoomCache | undefined;
};

function pickWallCacheForFace(face: Face, entries: readonly WallCacheEntry[]): WallSideRoomCache | undefined {
  const centroid = faceCentroid(face);
  if (!centroid || entries.length === 0) {
    return undefined;
  }
  const [cx, cy] = centroid;

  let bestCache: WallSideRoomCache | undefined;
  let bestDist = Infinity;
  for (const entry of entries) {
    const dist = distPointToSegment(cx, cy, entry.startX, entry.startY, entry.endX, entry.endY);
    if (dist < bestDist) {
      bestDist = dist;
      bestCache = entry.cache;
    }
  }
  return bestCache;
}

export function getWallSideRoomIdsFromBuildingGraph(
  wall: SolidWall,
  levelFloorElevation: number,
  levelTopOfWall: number,
  buildingGraph: BuildingGraph,
  rooms: Building["rooms"],
): WallSideRoomCache | undefined {
  const { startX, startY, endX, endY } = wall.mass.path;
  const canonicalPlane = planeCanonicalFromSegment(startX, startY, endX, endY, planeNewXY());
  if (!canonicalPlane) {
    return undefined;
  }

  const planeHashKey = planeHash(canonicalPlane);
  const buildingPlane = buildingGraph.buildingPlanes[planeHashKey];
  if (!buildingPlane) {
    return undefined;
  }

  const [pSx, pSy] = planeProject(canonicalPlane, startX, startY, levelFloorElevation);
  const [pEx, pEy] = planeProject(canonicalPlane, endX, endY, levelFloorElevation + levelTopOfWall);
  const searchAABB = aabb2New(Math.min(pSx, pEx), Math.min(pSy, pEy), Math.max(pSx, pEx), Math.max(pSy, pEy));
  aabb2Grow(searchAABB, -DIST_EPSILON, -DIST_EPSILON);

  const canonicalLeftRoomIds: Record<string, true> = {};
  const canonicalRightRoomIds: Record<string, true> = {};

  for (const topologyId of buildingPlane.planeTopologyIds) {
    const topology = buildingGraph.faceTopologies[topologyId];
    if (!topology) {
      continue;
    }
    for (const hit of topology.faceNodePlaneRTree.search(searchAABB)) {
      const faceNode = buildingGraph.faceNodes[hit.faceNodeId];
      if (!faceNode) {
        continue;
      }
      const parentHandle = faceNodeGetVolumeNode(faceNode, buildingGraph)?.parentHandle;
      if (parentHandle?.type !== "ROOM") {
        continue;
      }
      const target =
        faceNode.outwardSideDirection === "WALL_PLANE_POSITIVE_NORMAL" ? canonicalLeftRoomIds : canonicalRightRoomIds;
      target[parentHandle.roomId] = true;
    }
  }

  const [rightNormalX, rightNormalY] = line2Normal(startX, startY, endX, endY, true);
  const [canonicalNormalX, canonicalNormalY] = planeNormal(canonicalPlane);
  const canonicalRightMatchesWallRight = rightNormalX * canonicalNormalX + rightNormalY * canonicalNormalY > 0;

  /**
   * @TODO WALLS V2 - this is very similar to the selection logic in generateWallMasses2. We should consider refactoring
   * to a common function or set of utilities. Wall Framing generation uses a simililarly unique logic for picking the room id.
   */
  const pickRoomId = (ids: Record<string, true>): string | undefined => {
    const keys = Object.keys(ids);
    if (keys.length === 0) {
      return undefined;
    }
    if (keys.length === 1) {
      return keys[0];
    }

    const sortedKeys = [...keys].sort((roomIdA, roomIdB) => {
      const roomClassA = rooms[roomIdA]?.attributes.roomClass ?? Number.POSITIVE_INFINITY;
      const roomClassB = rooms[roomIdB]?.attributes.roomClass ?? Number.POSITIVE_INFINITY;
      if (roomClassA !== roomClassB) {
        return roomClassA - roomClassB;
      }
      return roomIdA.localeCompare(roomIdB);
    });
    return sortedKeys[0];
  };

  const leftCanonicalRoomId = pickRoomId(canonicalLeftRoomIds);
  const rightCanonicalRoomId = pickRoomId(canonicalRightRoomIds);
  const [leftRoomId, rightRoomId] = canonicalRightMatchesWallRight
    ? [leftCanonicalRoomId, rightCanonicalRoomId]
    : [rightCanonicalRoomId, leftCanonicalRoomId];

  return {
    leftRoomId,
    rightRoomId,
    midX: (startX + endX) / 2,
    midY: (startY + endY) / 2,
    rightNormalX,
    rightNormalY,
  };
}

export function resolveFaceRoomId(
  face: Face,
  incomingRoomId: string,
  rooms: Building["rooms"],
  wallPlanesOn: boolean,
  cache: WallSideRoomCache | undefined,
): string | undefined {
  if (!wallPlanesOn) {
    return rooms[incomingRoomId] ? incomingRoomId : undefined;
  }
  if (!cache) {
    return undefined;
  }

  if (planeIsHorizontal(face.plane)) {
    const centroid = faceCentroid(face);
    if (!centroid) {
      return undefined;
    }
    const [cx, cy] = centroid;
    const dot = (cx - cache.midX) * cache.rightNormalX + (cy - cache.midY) * cache.rightNormalY;
    return dot >= 0 ? cache.rightRoomId : cache.leftRoomId;
  }

  const [nx, ny] = planeNormal(face.plane);
  const sideDot = nx * cache.rightNormalX + ny * cache.rightNormalY;
  if (Math.abs(sideDot) < DIST_EPSILON) {
    return undefined;
  }
  return sideDot > 0 ? cache.rightRoomId : cache.leftRoomId;
}

function collectLOD2WallGeomFromWallSystems(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallSystems: RWTable<WallSystem>,
  rooms: Building["rooms"],
  wallSweeps: RWTable<WallSweepSystem>,
  wallCatalogSettings: WallCatalogSettings,
  mirror: boolean,
  wallPlanesOn: boolean,
  buildingGraph: BuildingGraph,
  levels: Building["levels"],
  out: GeometryCollections,
) {
  for (const wallSystemId in wallSystems) {
    const wallSystem: WallSystem = wallSystems[wallSystemId];
    collectLOD2WallGeomFromWallSystem(
      wallDatumAbstractionProvider,
      wallSystem,
      rooms,
      wallSweeps[wallSystemId],
      wallCatalogSettings,
      mirror,
      wallPlanesOn,
      buildingGraph,
      levels,
      wallSystemId,
      out,
    );
  }
}

const exteriorSideIncludeFaceConditions = {
  left: true,
  right: true,
  top: false,
  bottom: false,
  start: false,
  end: false,
  leftHole: false,
  rightHole: false,
};

const exteriorCapIncludeFaceConditions = {
  left: false,
  right: false,
  top: true,
  bottom: true,
  start: true,
  end: true,
  leftHole: true,
  rightHole: true,
};

const interiorIncludeFaceConditions = {
  left: true,
  right: true,
  top: false,
  bottom: false,
  start: true,
  end: true,
  leftHole: false,
  rightHole: false,
};

function addGeomWithMaterial<T extends Face | Extrusion>(
  material: PaletteMaterialSlot | undefined,
  materialTransformation: UVTransformation | undefined,
  geom: T | T[],
  out: Record<string, GeomSetWithMaterial<T>>,
  hashOverride?: string,
) {
  const hash = hashOverride ?? hashMaterialWithUV(material, materialTransformation);
  if (!out[hash]) {
    out[hash] = {
      material,
      materialTransformation,
      geom: new Set(),
    };
  }
  for (const g of Array.isArray(geom) ? geom : [geom]) {
    out[hash].geom.add(g);
  }
}

function addFacesCategory(
  faces: { [blockId: string]: FacesWithMaterial[] },
  out: Record<string, GeomSetWithMaterial<Face>>,
) {
  for (const blockId in faces) {
    const facesWithMaterial = faces[blockId];
    for (const { material, materialTransformation, faces } of facesWithMaterial) {
      addGeomWithMaterial(material, materialTransformation, faces, out);
    }
  }
}

function addInteriorFacesCategory(
  facesByRoom: { [roomId: string]: FacesWithMaterial[] },
  rooms: Building["rooms"],
  out: GeometryCollections["interiorByRoomAndMaterial"],
) {
  for (const roomId in facesByRoom) {
    const faces = facesByRoom[roomId];
    if (!rooms[roomId]) {
      continue;
    }

    for (const facesWithMaterial of faces) {
      const hash = `${roomId}-${hashMaterialWithUV(
        facesWithMaterial.material,
        facesWithMaterial.materialTransformation,
      )}`;
      if (!out[hash]) {
        out[hash] = {
          roomId,
          material: facesWithMaterial.material,
          materialTransformation: facesWithMaterial.materialTransformation,
          geom: new Set(),
        };
      }
      for (const face of facesWithMaterial.faces) {
        out[hash].geom.add(face);
      }
    }
  }
}

function bucketInteriorFinishFaces(
  facesByRoomId: { [roomId: string]: FacesWithMaterial[] },
  wallCacheEntries: readonly WallCacheEntry[],
  wallPlanesOn: boolean,
  rooms: Building["rooms"],
  out: GeometryCollections,
) {
  for (const roomId in facesByRoomId) {
    const faces = facesByRoomId[roomId];
    for (const facesWithMaterial of faces) {
      for (const face of facesWithMaterial.faces) {
        const wallSideRoomCache = pickWallCacheForFace(face, wallCacheEntries);
        const resolvedRoomId = resolveFaceRoomId(face, roomId, rooms, wallPlanesOn, wallSideRoomCache);
        const bucketRoomId = resolvedRoomId ?? "Unknown";
        const hash = `${bucketRoomId}-${hashMaterialWithUV(
          facesWithMaterial.material,
          facesWithMaterial.materialTransformation,
        )}`;
        if (!out.interiorByRoomAndMaterial[hash]) {
          out.interiorByRoomAndMaterial[hash] = {
            roomId: bucketRoomId,
            material: facesWithMaterial.material,
            materialTransformation: facesWithMaterial.materialTransformation,
            geom: new Set(),
          };
        }
        out.interiorByRoomAndMaterial[hash].geom.add(face);
      }
    }
  }
}

function collectLOD2WallGeomFromWallSystem(
  wallDatumAbstractionProvider: WallDatumAbstractionProvider,
  wallSystem: WallSystem,
  rooms: Building["rooms"],
  wallSweeps: WallSweepSystem | undefined,
  wallCatalogSettings: WallCatalogSettings,
  mirror: boolean,
  wallPlanesOn: boolean,
  buildingGraph: BuildingGraph,
  levels: Building["levels"],
  levelId: string,
  out: GeometryCollections,
) {
  if (!wallPlanesOn) {
    const wallFinishFaces = collectWallFinishFaces2({
      wallDatumAbstractionProvider,
      wallSystem,
      wallSweeps,
      wallCatalogSettings,
      mirror,
      includeInteriorFaceConditions: interiorIncludeFaceConditions,
    });

    addFacesCategory(wallFinishFaces.exteriorWallFaces, out.exteriorSideFacesByMaterialUV);
    addFacesCategory(wallFinishFaces.exteriorWallCapFaces, out.exteriorCapFacesByMaterialUV);

    for (const blockId in wallFinishFaces.exteriorWallExtrusions) {
      const ents = wallFinishFaces.exteriorWallExtrusions[blockId];
      for (const ent of ents) {
        addGeomWithMaterial(
          ent.material,
          ent.materialTransformation,
          ent.extrusion,
          out.exteriorExtrusionsByMaterialUV,
        );
      }
    }

    addInteriorFacesCategory(wallFinishFaces.interiorWallCapFaces, rooms, out.interiorByRoomAndMaterial);
    addInteriorFacesCategory(wallFinishFaces.interiorWallFaces, rooms, out.interiorByRoomAndMaterial);
    return;
  }

  const level = levels[levelId];

  const exteriorSides: WallFinishFaces = collectWallFinishFaces2({
    wallDatumAbstractionProvider,
    wallSystem,
    wallSweeps,
    wallCatalogSettings,
    mirror,
    includeExteriorFaceConditions: exteriorSideIncludeFaceConditions,
    includeInteriorFaceConditions: {},
  });

  addFacesCategory(exteriorSides.exteriorWallCapFaces, out.exteriorSideFacesByMaterialUV);
  addFacesCategory(exteriorSides.exteriorWallFaces, out.exteriorSideFacesByMaterialUV);

  for (const blockId in exteriorSides.exteriorWallExtrusions) {
    const ents = exteriorSides.exteriorWallExtrusions[blockId];
    for (const ent of ents) {
      addGeomWithMaterial(ent.material, ent.materialTransformation, ent.extrusion, out.exteriorExtrusionsByMaterialUV);
    }
  }

  const exteriorCaps: WallFinishFaces = collectWallFinishFaces2({
    wallDatumAbstractionProvider,
    wallSystem,
    wallSweeps,
    wallCatalogSettings,
    mirror,
    includeExteriorFaceConditions: exteriorCapIncludeFaceConditions,
    includeInteriorFaceConditions: {},
  });

  addFacesCategory(exteriorCaps.exteriorWallCapFaces, out.exteriorCapFacesByMaterialUV);

  const wallCacheEntries: WallCacheEntry[] = [];
  for (const wallId in wallSystem.walls) {
    const wall = wallSystem.walls[wallId];
    if (wall.wallType !== WallType.SOLID) {
      continue;
    }
    const { startX, startY, endX, endY } = wall.mass.path;
    let cache: WallSideRoomCache | undefined;
    if (level) {
      cache = getWallSideRoomIdsFromBuildingGraph(wall, level.floorElevation, level.topOfWall, buildingGraph, rooms);
    }
    wallCacheEntries.push({ startX, startY, endX, endY, cache });
  }

  const interior: WallFinishFaces = collectWallFinishFaces2({
    wallDatumAbstractionProvider,
    wallSystem,
    wallSweeps,
    wallCatalogSettings,
    mirror,
    includeExteriorFaceConditions: {},
    includeInteriorFaceConditions: interiorIncludeFaceConditions,
  });

  bucketInteriorFinishFaces(interior.interiorWallCapFaces, wallCacheEntries, wallPlanesOn, rooms, out);
  bucketInteriorFinishFaces(interior.interiorWallFaces, wallCacheEntries, wallPlanesOn, rooms, out);
}

function collectFacesFromRoofWallFaces(
  material: PaletteMaterialSlot | undefined,
  materialTransformation: UVTransformation | undefined,
  faces: RoofWallFaces,
  { exteriorSideFacesByMaterialUV: out }: GeometryCollections,
) {
  addGeomWithMaterial(material, materialTransformation, faces.leftExteriorFaces, out);
  addGeomWithMaterial(material, materialTransformation, faces.rightExteriorFaces, out);
}
